const API_BASE_URL = (
  process.env.REACT_APP_API_URL ?? '/api'
).replace(/\/+$/, '');

function joinPath(base: string, endpoint: string) {
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${base}${path}`;
}

function normalizeToArray<T>(body: any): T[] {
  if (Array.isArray(body)) return body as T[];
  if (Array.isArray(body?.data)) return body.data as T[];
  if (Array.isArray(body?.items)) return body.items as T[];
  if (Array.isArray(body?.results)) return body.results as T[];
  return [];
}

async function parseJsonSafely(res: Response) {
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) return null;
  try { return await res.json(); } catch { return null; }
}

export class ApiClient {
  private baseURL: string;
  private token: string | null = null;
  private defaultTimeoutMs = 15000; // 15s

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
  }

  setToken(token: string | null) {
    this.token = token;
    if (typeof window === 'undefined') return;
    if (token) localStorage.setItem('accessToken', token);
    else localStorage.removeItem('accessToken');
  }

  private buildUrl(endpoint: string) {
    return joinPath(this.baseURL, endpoint);
  }

  private async _requestWithoutAuth(endpoint: string, options: RequestInit = {}): Promise<any> {
    const url = this.buildUrl(endpoint);

    const hasBody = options.body !== undefined && options.body !== null;
    const headers: Record<string, string> = {
      ...(options.headers as any),
    };
    // Body가 있을 때만 Content-Type 기본값
    if (hasBody && !('Content-Type' in headers)) {
      headers['Content-Type'] = 'application/json';
    }
    // JSON 기대
    if (!('Accept' in headers)) headers['Accept'] = 'application/json';

    // 타임아웃
    const ac = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
    const to = ac ? setTimeout(() => ac.abort(), this.defaultTimeoutMs) : undefined;

    try {
      const res = await fetch(url, {
        ...options,
        headers,
        redirect: 'manual' as RequestRedirect,
        signal: ac?.signal,
      });

      if (!res.ok) {
        const errJson = await parseJsonSafely(res);
        if (errJson?.message) {
          throw new Error(Array.isArray(errJson.message) ? errJson.message.join(', ') : errJson.message);
        }
        throw new Error(`HTTP ${res.status} ${res.statusText}`);
      }

      const json = await parseJsonSafely(res);
      if (json !== null) return json;

      const text = await res.text();
      try { return JSON.parse(text); } catch { return text || {}; }
    } finally {
      if (to) clearTimeout(to);
    }
  }

  private async _request(endpoint: string, options: RequestInit = {}, timeoutMs?: number, as?: 'blob'): Promise<any> {
    const url = this.buildUrl(endpoint);

    const hasBody = options.body !== undefined && options.body !== null;
    const headers: Record<string, string> = {
      ...(options.headers as any),
    };
    // Body가 있을 때만 Content-Type 기본값 (FormData 는 브라우저가 경계값과 함께 붙인다)
    const isForm = typeof FormData !== 'undefined' && options.body instanceof FormData;
    if (hasBody && !isForm && !('Content-Type' in headers)) {
      headers['Content-Type'] = 'application/json';
    }
    // JSON 기대
    if (!('Accept' in headers)) headers['Accept'] = 'application/json';
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    // 현재 페이지 경로 전송 (활동 추적용)
    if (typeof window !== 'undefined') {
      headers['X-Page'] = window.location.pathname;
    }

    // 타임아웃 (커스텀 또는 기본값)
    const actualTimeout = timeoutMs ?? this.defaultTimeoutMs;
    const ac = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
    const to = ac ? setTimeout(() => ac.abort(), actualTimeout) : undefined;

    try {
      // 리다이렉트 자동 추종 안 함(HTML 섞이는 것 방지)
      const res = await fetch(url, {
        ...options,
        headers,
        redirect: 'manual' as RequestRedirect,
        signal: ac?.signal,
      });

      // 3xx: 이동하지 않고 에러로 처리 (로그인 페이지 비활성 상태)
      if (res.status >= 300 && res.status < 400) {
        const loc = res.headers.get('Location') || '';
        throw new Error(`Redirected (${res.status}) ${loc}`);
      }

      // 401: 토큰 갱신 시도
      if (res.status === 401) {
        // 토큰 갱신 API 호출이 아닌 경우에만 갱신 시도
        if (!endpoint.includes('/auth/refresh') && this.token) {
          try {
            const refreshResponse = await this._requestWithoutAuth('/auth/refresh', {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${this.token}` }
            });
            
            if (refreshResponse?.access_token) {
              this.setToken(refreshResponse.access_token);
              // 원래 요청 재시도
              return this._request(endpoint, options, timeoutMs, as);
            }
          } catch (refreshError) {
            // 토큰 갱신 실패 시 로그아웃 처리
            this.setToken(null);
            if (typeof window !== 'undefined') {
              window.location.href = '/login';
            }
            throw new Error('Token refresh failed');
          }
        }
        
        this.setToken(null);
        throw new Error('Unauthorized');
      }

      if (!res.ok) {
        const errJson = await parseJsonSafely(res);
        if (errJson?.message) {
          throw new Error(Array.isArray(errJson.message) ? errJson.message.join(', ') : errJson.message);
        }
        throw new Error(`HTTP ${res.status} ${res.statusText}`);
      }

      const ct = res.headers.get('content-type') || '';
      if (as === 'blob' && !ct.includes('text/html')) return res.blob();
      if (ct.includes('text/html')) {
        // SPA/HTML이 떨어지는 상황은 비정상 → 에러
        throw new Error('Unexpected HTML response');
      }

      // JSON 우선
      const json = await parseJsonSafely(res);
      if (json !== null) return json;

      // 비-JSON 응답
      const text = await res.text();
      try { return JSON.parse(text); } catch { return text || {}; }
    } finally {
      if (to) clearTimeout(to);
    }
  }

  // ✔ 객체(JSON) 그대로 받고 싶을 때
  async getJSON<T>(endpoint: string): Promise<T> {
    return this._request(endpoint, { method: 'GET' }) as Promise<T>;
  }

  // ✔ 리스트 화면: 항상 배열 보장
  async getList<T>(endpoint: string): Promise<T[]> {
    const body = await this._request(endpoint, { method: 'GET' });
    return normalizeToArray<T>(body);
  }

  // 기존 시그니처 유지(객체/리스트 모두 가능)
  async get<T>(endpoint: string): Promise<T> {
    return this._request(endpoint, { method: 'GET' }) as Promise<T>;
  }

  async post<T>(endpoint: string, data: any, options?: { timeoutMs?: number }): Promise<T> {
    return this._request(endpoint, { method: 'POST', body: JSON.stringify(data) }, options?.timeoutMs) as Promise<T>;
  }

  async put<T>(endpoint: string, data: any): Promise<T> {
    return this._request(endpoint, { method: 'PUT', body: JSON.stringify(data) }) as Promise<T>;
  }

  async patch<T>(endpoint: string, data: any): Promise<T> {
    return this._request(endpoint, { method: 'PATCH', body: JSON.stringify(data) }) as Promise<T>;
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this._request(endpoint, { method: 'DELETE' }) as Promise<T>;
  }

  // ✔ 파일 올리기 (multipart) — 큰 파일을 생각해 기본 60초
  async upload<T>(endpoint: string, form: FormData, options?: { timeoutMs?: number }): Promise<T> {
    return this._request(endpoint, { method: 'POST', body: form }, options?.timeoutMs ?? 60_000) as Promise<T>;
  }

  // ✔ 파일 올리고 파일 받기 (PPTX 만들기 등)
  async uploadBlob(endpoint: string, form: FormData, options?: { timeoutMs?: number }): Promise<Blob> {
    return this._request(endpoint, { method: 'POST', body: form, headers: { Accept: '*/*' } }, options?.timeoutMs ?? 120_000, 'blob') as Promise<Blob>;
  }

  // ✔ 파일 받기 — 응답을 Blob 으로
  async getBlob(endpoint: string): Promise<Blob> {
    return this._request(endpoint, { method: 'GET', headers: { Accept: '*/*' } }, 60_000, 'blob') as Promise<Blob>;
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
